import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IMG_URl } from "../../../../common/img_url";
import { indexStore } from "../../../../store/indexStore";
import { getUniqueCategories } from "../../../utils/UtilsFunctions";
import ImageSelected from "../../../Uploaders/ImageSelected";
import { Button } from "../../../ui/button";
import { DialogClose } from "src/Components/ui/dialog";
import { Form, FormField } from "src/Components/ui/form";
import FormFieldItem from "../../../../common/form/FormFieldItem";
import { Label } from "src/Components/ui/label";
import ItemTabs from "../ItemTabs";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Switch } from "src/Components/ui/switch";
import { Input } from "src/Components/ui/input";
import { ComboboxForm } from "src/Components/selectbox/ComboBoxForm";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Package, FolderTree, DollarSign, Tag, Image as ImageIcon, Sparkles } from "lucide-react";
import { Separator } from "../../../ui/separator";

function ItemForm(props) {
  const { nameLang, itemLoading, categories, menus } = indexStore();
  const { t } = useTranslation();
  const [image, setImage] = useState("");
  const [deleteImage, setDeleteImage] = useState(0);

  const dialogCloseRef = useRef(null);
  // make validation and form fields values conditionally
  const formSchema = z.object({
    name_ar: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    name_en: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    description_ar: z
      .string({
        required_error: `${t("gloabl.description")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.description")} ${t(
          "errorMessage.fieldCharsLength"
        )}.`,
      }),
    description_en: z
      .string({
        required_error: `${t("gloabl.description")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.description")} ${t(
          "errorMessage.fieldCharsLength"
        )}.`,
      }),
    category_id: z.number({
      required_error: `${t("gloabl.menu")} ${t("errorMessage.fieldRequired")}.`,
    }),
    price: z.union([
      z
        .string()
        .refine((val) => !isNaN(Number(val)), {
          message: `${t("gloabl.price")} ${t("errorMessage.fieldRequired")}.`,
        })
        .transform((val) => Number(val)),
      z.number({
        required_error: `${t("gloabl.price")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      }),
    ]),
    price_offer: z
      .union([
        z
          .string()
          .refine((val) => !isNaN(Number(val)))
          .transform((val) => Number(val)),
        z.number(),
      ])
      .optional(),
    new: z.any().optional(),
    popular: z.any().optional(),
    unavailable: z.any().optional(),
    hidden: z.any().optional(),
    image: z
      .union([
        z.instanceof(File).refine((file) => file.size < 4 * 1024 * 1024, {
          message: "File size must be less than 4MB",
          required_error: `${t("gloabl.image")} ${t(
            "errorMessage.fieldRequired"
          )}.`,
        }),
        z.string({
          required_error: `${t("gloabl.image")} ${t(
            "errorMessage.fieldRequired"
          )}.`,
        }),
      ])
      .optional(),
  });

  // 1. Define your form.
  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      ...props.datasource,
    },
  });

  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };

  const onSubmit = (values) => {
    let submitValues = {
      ...values,
      deleteImage: deleteImage,
      unavailable: values.unavailable ? 1 : 0,
      hidden: values.hidden ? 1 : 0,
      popular: values.popular ? 1 : 0,
      new: values.new ? 1 : 0,
    };
    // console.log(submitValues);

    props.onSubmit(submitValues);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };
  useEffect(() => {
    if (props.editMode) {
      setImage(props.datasource.image ? IMG_URl + props.datasource.image : "");
    }
  }, [props.editMode]);

  const uniqueCategories = getUniqueCategories(categories, menus, nameLang);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Package className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.basic_information") || "Basic Information"}</CardTitle>
                <CardDescription>
                  {t("gloabl.basic_information_desc") || "Enter the item name and description in both languages"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <ItemTabs form={form} className="bg-transparent" />
          </CardContent>
        </Card>

        {/* Category Selection Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FolderTree className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.category")}</CardTitle>
                <CardDescription>
                  {t("gloabl.select_category_desc") || "Select the category this item belongs to"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="category_id"
              render={({ field }) => {
                return (
                  <FormFieldItem required label={t("gloabl.category")} form={form}>
                    <ComboboxForm
                      control={form.control}
                      name="category_id"
                      data={uniqueCategories}
                      {...field}
                      placeholder={t("gloabl.select") + " " + t("gloabl.category")}
                      lable={"displayName"}
                    />
                  </FormFieldItem>
                );
              }}
            />
          </CardContent>
        </Card>

        {/* Pricing Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <DollarSign className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.pricing") || "Pricing"}</CardTitle>
                <CardDescription>
                  {t("gloabl.pricing_desc") || "Set the item price and optional offer price"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <ProductPrice form={form} t={t} />
          </CardContent>
        </Card>

        {/* Tags/Status Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Tag className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.tags_status") || "Tags & Status"}</CardTitle>
                <CardDescription>
                  {t("gloabl.tags_status_desc") || "Configure item tags and availability status"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <ProductTags form={form} t={t} />
          </CardContent>
        </Card>

        {/* Image Card */}
        <Card className="border-2">
          <CardHeader className="pb-3 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <ImageIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg">{t("gloabl.image")}</CardTitle>
                <CardDescription>
                  {t("gloabl.image_desc_item") || "Upload an item image (max 4MB)"}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="image"
              render={({ field }) => {
                return (
                  <FormFieldItem label={t("gloabl.image")} form={form}>
                    <ImageSelected
                      form={form}
                      control={form.control}
                      name="image"
                      {...field}
                      editMode={props.editMode}
                      image={image}
                      setImage={setImage}
                      setDeleteImage={setDeleteImage}
                    />
                  </FormFieldItem>
                );
              }}
            />
          </CardContent>
        </Card>

        <Separator />

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={itemLoading}>
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit" disabled={itemLoading}>
            {itemLoading ? t("gloabl.processing") || "Processing..." : t("gloabl.save")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default ItemForm;

const ProductTags = ({ form, t }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name="popular"
        render={({ field }) => (
          <FormFieldItem form={form}>
            <div className="flex items-center gap-2">
              <Switch
                dir="ltr"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label>{t("gloabl.popular")}</Label>
            </div>
          </FormFieldItem>
        )}
      />
      <FormField
        control={form.control}
        name="new"
        render={({ field }) => (
          <FormFieldItem form={form}>
            <div className="flex items-center gap-2">
              <Switch
                dir="ltr"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label>{t("gloabl.new")}</Label>
            </div>
          </FormFieldItem>
        )}
      />
      <FormField
        control={form.control}
        name="unavailable"
        render={({ field }) => (
          <FormFieldItem form={form}>
            <div className="flex items-center gap-2">
              <Switch
                dir="ltr"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label>{t("gloabl.unavailable")}</Label>
            </div>
          </FormFieldItem>
        )}
      />{" "}
      <FormField
        control={form.control}
        name="hidden"
        render={({ field }) => (
          <FormFieldItem form={form}>
            <div className="flex items-center gap-2">
              <Switch
                dir="ltr"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label>{t("gloabl.hidden")}</Label>
            </div>
          </FormFieldItem>
        )}
      />
    </div>
  );
};
const ProductPrice = ({ form, t }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <FormField
        control={form.control}
        name="price"
        render={({ field }) => (
          <FormFieldItem required label={t("gloabl.price")} form={form}>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              {...field}
            />
          </FormFieldItem>
        )}
      />
      <FormField
        control={form.control}
        name="price_offer"
        render={({ field }) => (
          <FormFieldItem label={t("gloabl.price_offer")} form={form}>
            <Input 
              placeholder="0.00" 
              type="number" 
              step="0.01"
              min="0"
              {...field} 
            />
          </FormFieldItem>
        )}
      />
    </div>
  );
};

/*
        formatter={(value) =>
          ` ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        }
        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
*/
